<template>
    <!-- the game log of a player: one row per match, the latest first -->
    <section class="cl-section game-log">
        <section-heading title="Αγώνες" icon="mdi-format-list-numbered" :count="rows.length || null" />
        <div v-if="!rows.length" class="cl-empty">Δεν υπάρχουν αγώνες σε αυτή τη φάση.</div>
        <v-data-table
            v-else
            :headers="headers"
            :items="rows"
            class="cl-table game-log__table"
            hide-default-footer
            disable-sort
            :mobile-breakpoint="0"
            :items-per-page="-1"
            dark
            @click:row="item => item.matchId && $router.push({ name: 'Match', params: { id: item.matchId } })"
        >
            <!-- every foul of the match: personal, technical and unsportsmanlike -->
            <template v-slot:item.fouls="{ item }">
                {{ foulsOf(item) }}
            </template>
            <template v-slot:item.team="{ item }">
                <span class="game-log__opponent">
                    <span class="cl-logo game-log__logo"><img :src="`${assetsUrl}/${item.opponentTeamLogo}`" alt=""></span>
                    <span class="game-log__names">
                        <span class="game-log__name">vs {{ item.opponentTeamName }}</span>
                        <span class="game-log__sub">{{ formatDate(item.matchDatetime) }}<template v-if="item.round"> · MD {{ item.round }}</template></span>
                    </span>
                </span>
            </template>
            <template v-slot:item.playTimeInSecs="{ item }">
                <span class="game-log__muted">{{ minutes(item.playTimeInSecs) }}</span>
            </template>
            <template v-slot:item.points="{ item }">
                <span class="game-log__points">{{ item.points }}</span>
            </template>
            <template v-slot:item.two="{ item }">
                <span class="game-log__shot">
                    <span>{{ item.twoPointsMade }}/{{ item.twoPointAttempts }}</span>
                    <span class="game-log__pct">{{ pct(item.twoPointsMade, item.twoPointAttempts) }}</span>
                </span>
            </template>
            <template v-slot:item.three="{ item }">
                <span class="game-log__shot">
                    <span>{{ item.threePointsMade }}/{{ item.threePointAttempts }}</span>
                    <span class="game-log__pct">{{ pct(item.threePointsMade, item.threePointAttempts) }}</span>
                </span>
            </template>
            <template v-slot:item.free="{ item }">
                <span class="game-log__shot">
                    <span>{{ item.freeThrowsMade }}/{{ item.freeThrowAttempts }}</span>
                    <span class="game-log__pct">{{ pct(item.freeThrowsMade, item.freeThrowAttempts) }}</span>
                </span>
            </template>
            <template v-slot:item.pir="{ item }">
                <span class="game-log__eff">{{ item.pir }}</span>
            </template>
        </v-data-table>
    </section>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import SectionHeading from '@/components/SectionHeading'
    import { foulsOf } from '@/utils/teamStats'

    export default {
        components: { SectionHeading },
        props: {
            stats: {
                type: Array,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                headers: [
                    { text: 'Αντίπαλος', value: 'team', align: 'start' },
                    { text: 'MIN', value: 'playTimeInSecs', align: 'center' },
                    { text: 'PTS', value: 'points', align: 'center' },
                    { text: '2P', value: 'two', align: 'center' },
                    { text: '3P', value: 'three', align: 'center' },
                    { text: 'FT', value: 'free', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'center' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'center' },
                    { text: 'TR', value: 'rebounds', align: 'center' },
                    { text: 'AST', value: 'assists', align: 'center' },
                    { text: 'STL', value: 'steals', align: 'center' },
                    { text: 'TO', value: 'turnovers', align: 'center' },
                    { text: 'BLK', value: 'blockedShots', align: 'center' },
                    { text: 'FC', value: 'fouls', align: 'center' },
                    { text: 'EFF', value: 'pir', align: 'center' }
                ]
            }
        },
        computed: {
            rows() {
                return [...(this.stats || [])].sort((a, b) => momentTimezone(b.matchDatetime).valueOf() - momentTimezone(a.matchDatetime).valueOf())
            }
        },
        methods: {
            foulsOf,
            formatDate(date) {
                return date ? momentTimezone(date).tz('Europe/Athens').locale('el').format('D MMM YYYY') : ''
            },
            pct(made, attempts) {
                return attempts ? `${Math.round((made / attempts) * 100)}%` : '–'
            },
            minutes(secs) {
                if (!secs || typeof secs !== 'number') return '–'
                const m = Math.floor(secs / 60)
                const s = Math.floor(secs % 60)
                return `${m}:${s < 10 ? '0' : ''}${s}`
            }
        }
    }
</script>

<style scoped>
.game-log__table ::v-deep tbody tr {
    cursor: pointer;
}

.game-log__table ::v-deep .v-data-table__wrapper > table > thead > tr > th,
.game-log__table ::v-deep .v-data-table__wrapper > table > tbody > tr > td {
    padding: 0 7px !important;
    white-space: nowrap;
}

.game-log__table ::v-deep .v-data-table__wrapper > table > thead > tr > th:first-child,
.game-log__table ::v-deep .v-data-table__wrapper > table > tbody > tr > td:first-child {
    padding-left: 14px !important;
    background: #232323 !important;
}

.game-log__table ::v-deep .v-data-table__wrapper > table > thead > tr > th:first-child {
    background: #1b1b1b !important;
}

.game-log__table ::v-deep .v-data-table__wrapper > table > tbody > tr > td {
    height: 50px !important;
    font-size: 0.85rem !important;
}

.game-log__opponent {
    display: flex;
    align-items: center;
    gap: 10px;
}

.game-log__logo {
    width: 30px;
    height: 30px;
}

.game-log__names {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.game-log__name {
    max-width: 200px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
}

.game-log__sub {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.55);
}

.game-log__muted {
    color: rgba(255, 255, 255, 0.75);
    font-variant-numeric: tabular-nums;
}

.game-log__points {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.25rem;
}

.game-log__shot {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.15;
}

.game-log__pct {
    font-size: 0.68rem;
    color: rgba(255, 255, 255, 0.5);
}

.game-log__eff {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.2rem;
    color: #03e5b7;
}

@media (max-width: 700px) {
    .game-log__name {
        max-width: 120px;
    }
}
</style>
